import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { AlertTriangle, AlertCircle, TrendingDown, ShoppingCart, Package, RefreshCw } from 'lucide-react';

// v5.0 - Recommandations basées sur consommation réelle (OUT_PRODUCTION)
const ReorderPage = () => {
    const { api, hasPermission } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const canView = hasPermission?.('view_stock_forecasting') ?? false;

    useEffect(() => {
        if (canView) {
            fetchData();
        }
    }, [canView]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await api().get('/reorder/recommendations');
            setData(res.data);
        } catch (error) {
            toast.error('Erreur lors du chargement des recommandations');
        } finally {
            setLoading(false);
        }
    };

    if (!canView) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-muted-foreground">Accès non autorisé</p>
            </div>
        );
    }

    const renderProductRow = (item) => {
        const isCritical = item.urgency === 'CRITIQUE';
        const isUrgent = item.urgency === 'URGENT';
        
        return (
            <TableRow 
                key={item.product_id} 
                className={isCritical ? 'bg-red-50' : isUrgent ? 'bg-orange-50' : 'bg-yellow-50'}
            >
                <TableCell>
                    <div className="flex items-center gap-2">
                        {isCritical ? (
                            <AlertTriangle className="w-4 h-4 text-red-500" />
                        ) : isUrgent ? (
                            <AlertCircle className="w-4 h-4 text-orange-500" />
                        ) : (
                            <TrendingDown className="w-4 h-4 text-yellow-600" />
                        )}
                        <span className="font-medium">{item.product_name}</span>
                    </div>
                </TableCell>
                <TableCell className="text-gray-500">
                    {item.category_name}
                </TableCell>
                <TableCell className="text-right font-mono">
                    {item.stock_display}
                </TableCell>
                <TableCell className="text-right font-mono">
                    {item.consumption_display || '—'}
                </TableCell>
                <TableCell className="text-right">
                    <span className={isCritical ? 'text-red-600 font-bold' : isUrgent ? 'text-orange-600 font-medium' : 'text-yellow-600'}>
                        {item.days_until_stockout?.toFixed(1) || '—'} j
                    </span>
                </TableCell>
                <TableCell className="text-right font-mono font-medium">
                    {item.packs_to_order ? (
                        <span>{item.packs_to_order} × {item.pack_description}</span>
                    ) : '—'}
                </TableCell>
                <TableCell className="text-right font-mono font-medium text-primary">
                    {item.estimated_cost_formatted || '—'}
                </TableCell>
            </TableRow>
        );
    };

    const hasRecommendations = data && (
        data.critical_items?.length > 0 || 
        data.urgent_items?.length > 0 || 
        data.normal_items?.length > 0
    );

    return (
        <div className="space-y-6" data-testid="reorder-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">Réapprovisionnement</h1>
                    <p className="text-muted-foreground">
                        Recommandations basées sur la consommation réelle
                    </p>
                </div>
                <Button variant="outline" onClick={fetchData} disabled={loading}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Actualiser
                </Button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
                </div>
            ) : !hasRecommendations ? (
                <Card>
                    <CardContent className="py-12 text-center">
                        <Package className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                        <p className="text-lg font-medium text-gray-600">
                            {data?.message || 'Aucune recommandation pour le moment'}
                        </p>
                        <p className="text-sm text-gray-500 mt-2">
                            {data?.message 
                                ? 'Validez une journée de ventes pour générer la consommation.' 
                                : 'Les recommandations apparaîtront après validation des ventes'}
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <>
                    {/* Résumé */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <Card className="bg-red-50 border-red-200">
                            <CardContent className="pt-6">
                                <div className="flex items-center gap-2">
                                    <AlertTriangle className="w-5 h-5 text-red-500" />
                                    <span className="text-sm font-medium text-red-700">Critiques</span>
                                </div>
                                <p className="text-2xl font-bold text-red-600 mt-2">
                                    {data.critical_items?.length || 0}
                                </p>
                                <p className="text-xs text-red-500">≤ 2 jours de stock</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-orange-50 border-orange-200">
                            <CardContent className="pt-6">
                                <div className="flex items-center gap-2">
                                    <AlertCircle className="w-5 h-5 text-orange-500" />
                                    <span className="text-sm font-medium text-orange-700">Urgents</span>
                                </div>
                                <p className="text-2xl font-bold text-orange-600 mt-2">
                                    {data.urgent_items?.length || 0}
                                </p>
                                <p className="text-xs text-orange-500">2-5 jours de stock</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-yellow-50 border-yellow-200">
                            <CardContent className="pt-6">
                                <div className="flex items-center gap-2">
                                    <TrendingDown className="w-5 h-5 text-yellow-600" />
                                    <span className="text-sm font-medium text-yellow-700">À surveiller</span>
                                </div>
                                <p className="text-2xl font-bold text-yellow-600 mt-2">
                                    {data.normal_items?.length || 0}
                                </p>
                                <p className="text-xs text-yellow-600">5-7 jours de stock</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-primary/5 border-primary/20">
                            <CardContent className="pt-6">
                                <div className="flex items-center gap-2">
                                    <ShoppingCart className="w-5 h-5 text-primary" />
                                    <span className="text-sm font-medium text-primary">Budget estimé</span>
                                </div>
                                <p className="text-2xl font-bold text-primary mt-2">
                                    {data.total_estimated_cost_formatted}
                                </p>
                                <p className="text-xs text-muted-foreground">Pour couvrir 7 jours</p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Table des recommandations */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <ShoppingCart className="w-5 h-5" />
                                Produits à commander
                            </CardTitle>
                            <CardDescription>
                                Triés par urgence décroissante
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Produit</TableHead>
                                        <TableHead>Catégorie</TableHead>
                                        <TableHead className="text-right">Stock</TableHead>
                                        <TableHead className="text-right">Conso/jour</TableHead>
                                        <TableHead className="text-right">Jours restants</TableHead>
                                        <TableHead className="text-right">À commander</TableHead>
                                        <TableHead className="text-right">Coût estimé</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {data.critical_items?.map(item => renderProductRow(item))}
                                    {data.urgent_items?.map(item => renderProductRow(item))}
                                    {data.normal_items?.map(item => renderProductRow(item))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
};

export default ReorderPage;
